import { FC, FocusEvent } from 'react';

import { EAssetType, ESpecialOfferInternalViewModel } from 'constants/enums';
import { DEFAULT_BACKGROUND_COLOR, GRADIENT_INPUT_STYLE, PROMOTION_DEFAULTS } from 'views/OfferDesign/OfferDesign.consts';
import { OfferDesignFormikProps } from 'views/OfferDesign/OfferDesign.types';
import { getFieldError, getInputErrorProps, handleButtonSuffixUpload } from 'views/OfferDesign/OfferDesign.utils';

import AcGradientInput from 'components/AcGradientInput/AcGradientInput';
import Input from 'components/Input/Input';
import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';
import { ColorPicker } from 'components/ui/ColorPicker/color-picker';
import { Separator } from 'components/ui/separator';
import UploadImage from 'components/UploadImage/UploadImage';

const FONT_WEIGHT_OPTIONS = [
  { value: 'normal', label: 'Regular' },
  { value: 'bold', label: 'Bold' },
  { value: 'bolder', label: 'Bolder' }
];

type PromotionFormFieldsProps = OfferDesignFormikProps & {
  isNameUnique: boolean;
  isExternalIdUnique: boolean;
  shouldDisplaySubTitle?: boolean;
  specialOffersInternalViewModel?: ESpecialOfferInternalViewModel;
};

export const PromotionFormFields: FC<PromotionFormFieldsProps> = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue,
  isNameUnique,
  isExternalIdUnique,
  shouldDisplaySubTitle = false,
  specialOffersInternalViewModel
}) => {
  const uniqueFlags = { isNameUnique, isExternalIdUnique };
  const nameError = getFieldError('name', touched, errors, uniqueFlags, 'isNameUnique');
  const externalIdError = getFieldError('externalId', touched, errors, uniqueFlags, 'isExternalIdUnique');
  const maxCollectTextLength =
    specialOffersInternalViewModel === ESpecialOfferInternalViewModel.SPIN_X ||
    specialOffersInternalViewModel === ESpecialOfferInternalViewModel.CASH_FRENZY
      ? 25
      : 8;
  const collectTextError = touched.collectText ? errors.collectText : undefined;
  const backgroundImageError = touched.backgroundImage ? errors.backgroundImage : undefined;
  const collectTextAtMaxMessage = values.collectText?.length === maxCollectTextLength
    ? `Maximum ${maxCollectTextLength} characters reached`
    : undefined;
  const selectedWeight = FONT_WEIGHT_OPTIONS.find((o) => o.value === values.fontWeight) || FONT_WEIGHT_OPTIONS[0];

  const handleCollectTextBlur = (e: FocusEvent<HTMLInputElement>) => {
    if (!e.target.value.trim()) {
      setFieldValue('collectText', PROMOTION_DEFAULTS.collectText);
    }
    handleBlur(e);
  };
  const bgColorValue = typeof values.backgroundColor === 'object' && values.backgroundColor
    ? values.backgroundColor
    : { colorOne: (values.backgroundColor as string) || DEFAULT_BACKGROUND_COLOR };

  return (
    <div className="rounded-lg border border-base-border p-9">
      <h3 className="text-[20px] font-semibold text-zinc-900">Promotion</h3>

      <div className="flex gap-5 mt-4">
        <Input
          label="Name"
          isRequired
          name="name"
          placeholder="Name"
          value={values.name}
          onChange={handleChange}
          onBlur={handleBlur}
          {...getInputErrorProps(nameError)}
          className="flex-1"
        />
        <Input
          label="External ID"
          isRequired
          name="externalId"
          placeholder="External ID"
          value={values.externalId}
          onChange={handleChange}
          onBlur={handleBlur}
          {...getInputErrorProps(externalIdError)}
          className="flex-1"
        />
        <Input
          label="Free Offer Text"
          isRequired
          name="collectText"
          placeholder="Collect"
          value={values.collectText}
          onChange={handleChange}
          onBlur={handleCollectTextBlur}
          maxLength={maxCollectTextLength}
          {...getInputErrorProps(collectTextError)}
          description={collectTextError || collectTextAtMaxMessage}
          className="flex-1"
        />
      </div>

      <Separator className="mt-5 mb-9 bg-base-border" />

      <h3 className="text-[20px] font-semibold text-zinc-900">
        Promotion Design
      </h3>
      <p className="font-inter text-base font-normal text-base-mutedForeground mt-1.5 mb-6">
        Customize the promotion to fit your store theme.
      </p>

      {/* TODO: Aligned with Jonathan — replace AcGradientInput (MUI) with new component when available */}
      <div className={`w-1/2 ${GRADIENT_INPUT_STYLE}`}>
        <AcGradientInput
          header="Background Color"
          name="backgroundColor"
          defaultValue={bgColorValue}
          onChange={handleChange}
          setValue={setFieldValue}
          onBlur={handleBlur}
        />
      </div>

      <div className="mt-5">
        <UploadImage
          title="Background Image"
          isRequired
          className="w-full !flex flex-row px-5"
          onUpload={(url) => setFieldValue('backgroundImage', url)}
          selectedImageUrl={values.backgroundImage}
          forceType={EAssetType.BG_BUNDLE}
          hasError={!!backgroundImageError}
          errorMessage={backgroundImageError}
          TooltipContent={() => <span>Upload a background image for the promotion offer card</span>}
        />
      </div>

      <div className="mt-5">
        <UploadImage
          title="Button Suffix Badge"
          className="w-full !flex flex-row px-5"
          onUpload={(url, name, mimeType) => handleButtonSuffixUpload(setFieldValue, url, name, mimeType)}
          selectedImageUrl={values.buttonSuffixImage || values.buttonSuffixAnimation}
          forceType={EAssetType.BUTTON_ASSET_SUFFIX}
          TooltipContent={() => <span>Upload a button suffix badge image or animation</span>}
        />
      </div>

      <h3 className="text-[20px] font-semibold text-zinc-900 mt-9">Title</h3>

      <div className="mt-4">
        <Input
          label="Title Name"
          name="title"
          placeholder="Title"
          value={values.title}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      </div>

      <div className="flex gap-5 mt-4">
        <div className="flex-1">
          <ColorPicker
            label="Color"
            defaultValue={values.fontColor?.colorOne || PROMOTION_DEFAULTS.fontColor.colorOne}
            onChange={(color) => setFieldValue('fontColor', { colorOne: color })}
          />
        </div>
        <div className="flex-1">
          <Input
            label="Size"
            name="fontSize"
            type="number"
            value={values.fontSize}
            onChange={handleChange}
            onBlur={handleBlur}
          />
        </div>
        <div className="flex-1">
          <InputSelectDropdown
            label="Weight"
            options={FONT_WEIGHT_OPTIONS}
            defaultSelected={selectedWeight}
            onOptionChange={(value) => setFieldValue('fontWeight', value)}
          />
        </div>
      </div>

      {shouldDisplaySubTitle && (
        <>
          <h3 className="text-[20px] font-semibold text-zinc-900 mt-9">Subtitle</h3>

          <div className="mt-4">
            <Input
              label="Subtitle Text"
              name="subTitleText"
              placeholder="Subtitle"
              value={values.subTitleText}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </div>

          <div className="flex gap-5 mt-4 pb-4">
            <div className="flex-1">
              <ColorPicker
                label="Color"
                defaultValue={values.subTitleFontColor?.colorOne || PROMOTION_DEFAULTS.fontColor.colorOne}
                onChange={(color) => setFieldValue('subTitleFontColor', { colorOne: color })}
              />
            </div>
            <div className="flex-1">
              <Input
                label="Size"
                name="subTitleFontSize"
                type="number"
                value={values.subTitleFontSize}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </div>
            <div className="flex-1">
              <InputSelectDropdown
                label="Weight"
                options={FONT_WEIGHT_OPTIONS}
                defaultSelected={
                  FONT_WEIGHT_OPTIONS.find((o) => o.value === values.subTitleFontWeight) ||
                  FONT_WEIGHT_OPTIONS[0]
                }
                onOptionChange={(value) => setFieldValue('subTitleFontWeight', value)}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};
